<!-- ================= HEADER ================= -->
<div class="title" *ngIf="!inPopup || !isViewMode">
  <div class="flex items-center justify-end gap-3">
    <div class="flex gap-4">
      <button
        *ngIf="showHeaderUpload"
        (click)="onHeaderUploadClick()"
        class="btn-primary"
      >
        {{ 'DOCUMENT.UPLOAD_DOCUMENT' | translate }}
      </button>

      <app-back-button *ngIf="!inPopup"></app-back-button>
    </div>
  </div>
</div>

<!-- ================= REQUIRED DOCUMENT CHECKLIST (FO letter "Required Documents" tab) ================= -->
<section *ngIf="showRequiredDocChecklist" class="rounded-2xl p-2 sm:p-2 mb-0">
  <div *ngIf="requiredDocChecklist.length === 0" class="text-gray-400 text-center p-6">
    {{ 'DOCUMENT.NO_REQUIRED_DOCUMENTS_CONFIGURED' | translate }}
  </div>

  <app-table
    *ngIf="requiredDocChecklist.length > 0"
    [columns]="requiredDocChecklistColumns"
    [data]="requiredDocChecklist"
    [actions]="requiredDocChecklistActions"
    [enablePagination]="true"
    [pageSize]="10"
  >
  </app-table>
</section>

<!-- ================= TABLE ================= -->
<section *ngIf="!showRequiredDocChecklist" class="rounded-2xl p-2 sm:p-2 mb-0">
  <!-- EMPTY -->
  <div *ngIf="documents.length === 0" class="text-gray-400 text-center p-6">
    {{ 'DOCUMENT.NO_DOCUMENTS_UPLOADED' | translate }}
  </div>

  <!-- TABLE -->
  <app-table
    *ngIf="documents.length > 0"
    [columns]="columns"
    [data]="documents"
    [actions]="actions"
    [enablePagination]="true"
    [pageSize]="10"
  >
  </app-table>
</section>

<!-- ================= DELETE CONFIRMATION POPUP ================= -->
<app-popup
  [(show)]="showDeletePopup"
  title="DOCUMENT.DELETE_DOCUMENT"
  type="confirm"
  [message]="deleteMessage"
  confirmText="COMMON.DELETE"
  cancelText="COMMON.CANCEL"
  (confirmed)="confirmDelete()"
>
</app-popup>

<!-- ================= HISTORY POPUP (showHistoryPopupAction) ================= -->
<app-popup
  [(show)]="showHistoryPopup"
  title="DOCUMENT_HISTORY.TITLE"
  type="custom"
  width="w-full max-w-4xl"
>
  @if (showHistoryPopup && historyRow) {
    <app-document-history
      [inPopup]="true"
      [historyEntitySeq]="historyRow.entySeq"
      [historyDocType]="historyRow.docTyp"
      [historyEntyTyp]="moduleSeq"
    ></app-document-history>
  }
</app-popup>

<!-- ================= UPDATE HARD COPY DATE (FOL issue documents — LNLY_AM) ================= -->
<app-popup
  [(show)]="showHardCopyDatePopup"
  title="DOCUMENT.UPDATE_HARD_COPY_DATE"
  type="form"
  [fields]="hardCopyDateFields"
  [data]="hardCopyDateFormData"
  [onSave]="handleHardCopyDateSave"
></app-popup>

<!-- ================= UPLOAD MODAL ================= -->
@if (showUploadModal) {
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" *ngIf="!isViewMode">
    <div class="bg-white rounded-2xl w-[90%] max-w-2xl p-8 relative">
      <!-- CLOSE -->
      <button
        type="button"
        (click)="closeUploadModal()"
        class="absolute top-6 end-6 w-10 h-10 rounded-full border flex items-center justify-center hover:bg-gray-100"
      >
        ✕
      </button>

      <!-- TITLE -->
      <h2 class="text-xl font-semibold mb-6">
        {{ uploadModalTitle | translate }}
      </h2>

      <!-- ================= DYNAMIC FORM ================= -->
      <app-dynamic-form
        [fields]="docFields"
        [model]="docFormValue"
        (formChange)="onDocFormChange($event)"
        (formSubmit)="handleDynamicSubmit($event)"
      >
      </app-dynamic-form>

      <!-- ================= FILE ERROR ================= -->
      <div *ngIf="fileError" class="text-red-500 text-sm mb-2 mt-3">
        {{ fileError }}
      </div>

      <!-- ================= COMPRESSING ================= -->
      <div *ngIf="compressingFile" class="text-indigo-600 text-sm mb-2 mt-3">
        {{ 'DOCUMENT.COMPRESSING_FILE' | translate }}
      </div>

      <!-- ================= IMAGE PREVIEW ================= -->
      <div
        *ngIf="previewUrl"
        class="mt-4 mb-4 flex flex-col items-center gap-2 border border-dashed border-gray-300 rounded-xl p-4"
      >
        <img [src]="previewUrl" class="max-h-44 rounded-xl object-contain shadow" />

        <button
          type="button"
          (click)="previewUrl = null; selectedFile = null"
          class="text-xs text-red-500 hover:underline"
        >
          {{ 'DOCUMENT.REMOVE_IMAGE' | translate }}
        </button>
      </div>

      <!-- =============s==== FILE UPLOAD ================= -->
      <div
        *ngIf="!previewUrl"
        class="border-2 border-dashed border-gray-300 rounded-2xl h-44 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 transition mb-6 mt-4"
        (click)="fileInput.click()"
      >
        <img src="images/upload.png" class="w-12 mb-2 opacity-70" />

        <p class="text-gray-500 text-sm">{{ 'DOCUMENT.CLICK_TO_UPLOAD_FILE' | translate }}</p>
      </div>

      <!-- FILE NAME -->
      <p *ngIf="selectedFile && !previewUrl" class="text-xs text-green-600 mb-4 text-center">
        {{ selectedFile.name }}
      </p>

      <input
        #fileInput
        type="file"
        [accept]="accept"
        class="hidden"
        (change)="onFileSelected($event)"
      />
      <div class="flex gap-4 mt-4" *ngIf="!isViewMode">
        <button
          [disabled]="compressingFile"
          (click)="triggerDynamicSubmit()"
          class="px-6 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ 'DOCUMENT.SAVE' | translate }}
        </button>

        <button
          type="button"
          (click)="closeUploadModal()"
          class="px-6 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600"
        >
          {{ 'DOCUMENT.CANCEL' | translate }}
        </button>
      </div>
    </div>
  </div>
}
